import * as React from 'react';
import Table, { TablePaginationConfig } from 'antd/es/table';
import ConfigurationService from '../../Service/ConfigurationService';
import TreeMaps from "./TreeMaps";
import { formatUTCDateFromDateString } from '../../Helper/DateHelper';
import { Tree } from 'tree-wallet-client';
import { GlobalContext } from "../../Contexts/GlobalContext";
import { PaginationProps } from "antd/lib/pagination/Pagination";
import { Col, Empty, Pagination, Row } from "antd";
import { FilterValue } from 'antd/lib/table/interface';
import { SorterResult } from 'antd/es/table/interface';
import { pickBy } from "lodash";
import { Filter, MapPager } from "./MapPage";

interface TreeTableProps {
  language: string;
  trees?: Tree[];
  paginationConfig: PaginationProps;
  getTrees: Function;
  updateState: Function;
  onDeleteTree: Function;
  pagerData?: MapPager;
  mapRef: React.Ref<TreeMaps>;
  filter: Filter;
}

interface DataType {
  key: React.Key;
  name: string;
  age: number;
  address: string;
}

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string | undefined | null;
  sortOrder?: string | undefined | null;
  filters?: Record<string, FilterValue>;
}

interface FetchDataParams extends TableParams {
  pageSize?: number;
  currentPage?: number;
}

interface TreeTableState extends FetchDataParams {
  loading: boolean;
}

export default class TreeTable extends React.Component<TreeTableProps, TreeTableState> {
  declare context: React.ContextType<typeof GlobalContext>;
  static contextType = GlobalContext;

  constructor(props: TreeTableProps) {
    super(props);
    this.handleTableChange = this.handleTableChange.bind(this);
    this.handlePaginationChange = this.handlePaginationChange.bind(this);
    this.state = {
      sortField: undefined,
      sortOrder: undefined,
      pageSize: props.paginationConfig.pageSize,
      currentPage: props.paginationConfig.current,
      filters: undefined,
      loading: false,
    };
  }

  setLoading(loading: boolean) {
    this.setState({ loading });
  }

  getColumns() {
    return [
      {
        title: 'Date',
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (date: any) => {
          const dateStr = formatUTCDateFromDateString(date);
          return (
            <div style={{ textAlign: 'center' }}>
              <div>{dateStr.date}</div>
              <div>{dateStr.time}</div>
              <div>UTC</div>
            </div>
          )
        }
      },
      {
        title: 'Description',
        key: 'description',
        render: (record: Tree) => {
          const { language, mapRef } = this.props;
          const treeType = record.treeType && record.treeType;
          const photo = record.photos && record.photos.length > 0 && record.photos[0].imageUrl;
          const divStyle = { paddingBottom: '5px' };
          const translation = treeType.translations[language]
            ? treeType.translations[language]
            : treeType.translations.latin;
          return (
            <>
              <Row>
                {photo &&
                  <Col>
                    <img
                      src={`${ConfigurationService.urls().baseApiUrl}${photo.replace(/\.(jpg|jpeg)$/, '_thumb.$1')}`}
                      className='treelist-image-thumb'
                    />
                  </Col>
                }
                <Col style={{ paddingLeft: '10px' }}>
                  <Row>
                    <Col>
                      <div style={divStyle}>
                        <span style={{ fontWeight: 'bold' }}>ID</span> {record.id}
                      </div>
                      <div style={divStyle}>
                        <div>
                          <span>{record.numberPlanted}x </span>
                          <span>{translation}</span>&nbsp;
                        </div>
                        <div>
                          <span style={{ fontStyle: 'italic', fontWeight: 'bold' }}>({treeType.translations.latin})</span>
                        </div>
                      </div>
                      <div style={divStyle}>
                        {record.treeType?.fruitTree ? 'fruit tree' : 'shade tree'}
                      </div>
                      <div style={divStyle}>
                        <span style={{ fontWeight: 'bold' }}>planted by</span> {record.user.firstName} {record.user.lastName}
                      </div>
                      <div style={divStyle}>
                        <a onClick={() => {
                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                          // @ts-expect-error
                          mapRef?.current?.changePosition({
                            coordinates: [record.lng, record.lat],
                            treeId: record.id,
                            selected: true,
                            openPopup: true,
                          })
                        }}>
                          <div style={divStyle}>
                            <span style={{ marginRight: '5px' }}>Accuracy</span>
                            <span>{record.accuracy} meter{record.accuracy > 1 ? 's' : ''}</span>
                          </div>
                          <div style={divStyle}>
                            <span style={{ marginRight: '5px' }}>Altitude</span>
                            <span>{record.altitude ? `${Math.round(record.altitude)} meter${Math.abs(Math.round(record.altitude)) > 1 ? 's' : ''}` : '-'}</span>
                          </div>
                        </a>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </>
          )
        }
      },
      {
        title: 'Contract',
        key: 'contract',
        render: (text: string, record: any) => {
          return record.contract.description;
        }
      },
    ];
  }

  async handlePaginationChange(currentPage: number, pageSize: number) {
    this.setState({ loading: true });

    // Get the current filter from props
    const { filter } = this.props;

    // Update the parent's state with the new pagination values and current filter
    this.props.updateState({
      pager: {
        ...this.props.pagerData,
        currentPage,
        pageSize
      },
      filter // Update the filter in parent state
    });

    // Call the parent's getTrees function with the current filter and new pagination
    await this.props.getTrees(
      undefined,
      filter,
      {
        currentPage,
        pageSize
      }
    );

    this.setState({
      loading: false,
      currentPage,
      pageSize
    });
  }

  async handleTableChange(
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue>,
    sorter: SorterResult<DataType>,
  ) {
    const sortOrderValues = {
      'ascend': 'ASC',
      'descend': 'DESC',
    };

    this.setState({ loading: true });
    
    // Get current filter
    const currentFilter = { ...this.props.filter };

    // Prepare the updated filter with sorting
    const updatedFilter = {
      ...currentFilter,
      sortField: sorter.field ? String(sorter.field) : undefined,
      sortOrder: sorter.order ? sortOrderValues[sorter.order] : undefined
    };

    // Update parent state with the new filter and pagination
    this.props.updateState({
      pager: {
        ...this.props.pagerData,
        currentPage: pagination.current,
        pageSize: pagination.pageSize
      },
      filter: updatedFilter
    });

    // Call the parent's getTrees with the updated filter and pagination
    await this.props.getTrees(
      undefined,
      updatedFilter,
      {
        currentPage: pagination.current,
        pageSize: pagination.pageSize
      }
    );

    this.setState({
      loading: false,
      currentPage: pagination.current,
      pageSize: pagination.pageSize,
      sortField: sorter.field ? String(sorter.field) : undefined,
      sortOrder: sorter.order ? sortOrderValues[sorter.order] : undefined
    });
  }

  compileQueryParams(data?: FetchDataParams) {
    const { pageSize, currentPage, sortField, sortOrder } = this.state;

    return {
      currentPage: data?.currentPage ? data?.currentPage : currentPage,
      pageSize: data?.pageSize ? data.pageSize : pageSize,
      sortField: data?.sortField ? data.sortField : sortField,
      sortOrder: data?.sortOrder ? data.sortOrder : sortOrder,
    };
  }

  async fetchData(params?: FetchDataParams) {
    const { getTrees, filter } = this.props;

    // Include the current filter in the request
    const requestParams = {
      ...pickBy(params, v => v !== undefined),
      ...(filter ? { filter } : {}) // Include the filter from props if it exists
    };

    return await getTrees(undefined, undefined, requestParams);
  }

  async refreshData() {
    this.setState({ loading: true });
    const params = this.compileQueryParams();
    await this.fetchData(params);

    this.setState({
      ...params,
      loading: false,
    });
  }

  render() {
    const { paginationConfig, pagerData, trees } = this.props;
    const { loading } = this.state;

    if (!trees || trees.length === 0) {
      return (
        <div>
          <h3 style={{ textAlign: 'center' }}>No tree found</h3>
          <Empty image={Empty.PRESENTED_IMAGE_DEFAULT} />
        </div>
      )
    }

    console.log(trees);

    return (
      <>
        <Row>
          <Col xl={18}>
            <Pagination
              {...paginationConfig}
              current={pagerData?.currentPage}
              pageSize={pagerData?.pageSize}
              style={{ paddingBottom: '10px' }}
              onChange={this.handlePaginationChange}
            />
          </Col>
          <Col xl={6} style={{ textAlign: 'right', paddingRight: '20px' }}>
            <h3>{pagerData?.totalTreesPlanted} trees</h3>
          </Col>
        </Row>
        <Row>
          <Col style={{ overflow: 'scroll' }}>
            <Table
              rowKey={'id'}
              onChange={this.handleTableChange}
              columns={this.getColumns()}
              // force table re-render on state change
              dataSource={trees || []}
              pagination={false}
              loading={loading}
            />
          </Col>
        </Row>
        <Row>
          <Col xl={18}>
            <Pagination
              {...paginationConfig}
              current={pagerData?.currentPage}
              pageSize={pagerData?.pageSize}
              style={{ paddingBottom: '10px' }}
              onChange={this.handlePaginationChange}
            />
          </Col>
        </Row>
      </>
    );
  }
}
